import {
  inject,
  provideAppInitializer,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import {
  MESSENGER_AUTH,
  MESSENGER_CONFIG,
  MESSENGER_DESKTOP_NOTIFIER,
  MESSENGER_LIVE,
  MESSENGER_NOTIFICATION_REPOSITORY,
  MESSENGER_REALTIME,
  MESSENGER_REPOSITORY,
  MessengerFacade,
} from '@crm/messenger';
import { ExperienceContextService } from '../core/experience-context';
import {
  resolveMessengerRuntimeEnvironment,
  selectMessengerProvider,
} from '../core/capability/capability.resolver';
import { MockMessengerAuth, MockMessengerRealtime } from './messenger.providers.mock';
import { MockMessengerRepository } from './messenger.providers.mock';
import { MockNotificationRepository } from './messenger.providers.mock';
import { MelloMessengerHttpRepository } from './mello-messenger-http.repository';
import { MelloNotificationHttpRepository } from './mello-notification.repository';
import {
  loadMessengerRuntimeConfig,
  MessengerRuntime,
} from './messenger-runtime';
import { McsLiveMessages } from './mcs-live-messages';
import { MsalMessengerAuth } from './msal-messenger-auth';
import { SignalRMcsHubConnector } from './signalr-mcs-hub.connector';
import { WebNotificationAdapter } from './web-notification.adapter';

export function provideMelloMessenger(): (Provider | EnvironmentProviders)[] {
  return [
    {
      provide: MESSENGER_CONFIG,
      useFactory: () => inject(MessengerRuntime).config,
    },
    {
      provide: MESSENGER_AUTH,
      useFactory: () => {
        const runtime = inject(MessengerRuntime);
        const env = resolveMessengerRuntimeEnvironment(runtime.config);
        if (selectMessengerProvider(env) === 'mock') {
          return new MockMessengerAuth(
            inject(ExperienceContextService, { optional: true }) ?? undefined,
          );
        }
        return new MsalMessengerAuth({ config: () => runtime.config });
      },
    },
    {
      provide: MESSENGER_REPOSITORY,
      useFactory: () => {
        const runtime = inject(MessengerRuntime);
        const env = resolveMessengerRuntimeEnvironment(runtime.config);
        if (selectMessengerProvider(env) === 'mock') {
          return new MockMessengerRepository();
        }
        const auth = inject(MESSENGER_AUTH);
        return new MelloMessengerHttpRepository({
          config: () => runtime.config,
          tokenProvider: () => auth.accessToken(),
          mcsConnector: new SignalRMcsHubConnector(),
        });
      },
    },
    {
      provide: MESSENGER_NOTIFICATION_REPOSITORY,
      useFactory: () => {
        const runtime = inject(MessengerRuntime);
        const env = resolveMessengerRuntimeEnvironment(runtime.config);
        if (selectMessengerProvider(env) === 'mock') {
          return new MockNotificationRepository();
        }
        const auth = inject(MESSENGER_AUTH);
        return new MelloNotificationHttpRepository({
          config: () => runtime.config,
          tokenProvider: () => auth.accessToken(),
        });
      },
    },
    {
      provide: MESSENGER_DESKTOP_NOTIFIER,
      useFactory: () => new WebNotificationAdapter(),
    },
    {
      provide: MESSENGER_REALTIME,
      useFactory: () => {
        const runtime = inject(MessengerRuntime);
        const env = resolveMessengerRuntimeEnvironment(runtime.config);
        return selectMessengerProvider(env) === 'mock'
          ? new MockMessengerRealtime()
          : undefined;
      },
    },
    {
      provide: MESSENGER_LIVE,
      useFactory: () => {
        const runtime = inject(MessengerRuntime);
        const env = resolveMessengerRuntimeEnvironment(runtime.config);
        if (selectMessengerProvider(env) === 'mock') return null;
        const auth = inject(MESSENGER_AUTH);
        return new McsLiveMessages({
          config: () => runtime.config,
          tokenProvider: () => auth.accessToken(),
          connector: new SignalRMcsHubConnector(),
        });
      },
    },
    provideAppInitializer(async () => {
      const runtime = inject(MessengerRuntime);
      const auth = inject(MESSENGER_AUTH);
      const facade = inject(MessengerFacade);
      await loadMessengerRuntimeConfig(runtime);
      await auth.initialize();
      if (runtime.config.mode === 'remote' && auth.account()) void facade.startLive();
    }),
  ];
}
